/**
 * Select Patient data for the familiar modes' dialogs (requests-dentrix
 * I-14, requests-opendental R6, requests-eaglesoft D3) and the shared
 * patient verbs (`patient.last`, `patient.open.<target>`). The shell's
 * SelectPatientHost uses these; the pure parts are unit-tested here.
 *
 * The search query travels in a POST body (never a URL). The recent list
 * lives on the server per user and practice, so it survives reloads and
 * devices; this module never writes patient data to browser storage.
 */
import {
  RecentPatientMarkResultSchema,
  RecentPatientsSchema,
  SelectPatientResultSchema,
  type SelectPatientCriterion,
  type SelectPatientField,
  type SelectPatientResult,
  type SelectPatientRow,
} from '@velora/contracts/shell';
import type { HttpClient } from '@velora/sdk';

/** What a mode's dialog passes with its query (the box it is for, the other boxes, filters). */
export interface SelectSearchOptions {
  /** The field `query` is for; `auto` reads it like the top bar. */
  field?: SelectPatientField;
  /** Other filled-in boxes, combined with AND (Open Dental "multiple boxes"). */
  criteria?: readonly SelectPatientCriterion[];
  guarantorsOnly?: boolean;
  includeInactive?: boolean;
  includeArchived?: boolean;
  limit?: number;
  /** No query and no criteria: every patient in scope (Open Dental Get All). */
  all?: boolean;
}

/** The criteria a search sends; empty when nothing is worth asking. */
export function selectCriteria(query: string, options: SelectSearchOptions = {}) {
  const field = options.field ?? 'auto';
  const q = query.trim();
  const list: SelectPatientCriterion[] = [
    ...(q ? [{ field, q }] : []),
    ...(options.criteria ?? [])
      .map((c) => ({ field: c.field, q: c.q.trim() }))
      .filter((c) => c.q.length > 0),
  ];
  // A lone `auto` box needs two characters, as in the top bar.
  if (list.length === 1 && list[0]?.field === 'auto' && list[0].q.length < 2) return [];
  return list.slice(0, 8).map((c) => ({ ...c, q: c.q.slice(0, 80) }));
}

/** The search lists everyone in scope: no query, no boxes, and the mode asked for all (Get All). */
export function isGetAll(query: string, options: SelectSearchOptions = {}): boolean {
  return options.all === true && selectCriteria(query, options).length === 0 && !query.trim();
}

export async function fetchSelectPatients(
  client: HttpClient,
  criteria: readonly SelectPatientCriterion[],
  options: SelectSearchOptions,
  signal?: AbortSignal,
): Promise<SelectPatientResult> {
  return client.request('/shell/patients/select', {
    method: 'POST',
    body: {
      criteria,
      guarantorsOnly: options.guarantorsOnly ?? false,
      includeInactive: options.includeInactive ?? true,
      includeArchived: options.includeArchived ?? true,
      limit: options.limit ?? 40,
    },
    schema: SelectPatientResultSchema,
    ...(signal ? { signal } : {}),
  });
}

export async function fetchRecentPatients(
  client: HttpClient,
  signal?: AbortSignal,
): Promise<SelectPatientResult> {
  return client.request('/shell/patients/recent', {
    schema: RecentPatientsSchema,
    ...(signal ? { signal } : {}),
  });
}

export async function markRecentPatient(client: HttpClient, patientId: string): Promise<void> {
  await client.request('/shell/patients/recent', {
    method: 'POST',
    body: { patientId },
    schema: RecentPatientMarkResultSchema,
  });
}

/** "Last, First (Pref)" as the label every Select Patient grid has used so far. */
export function selectLabel(r: {
  lastName: string;
  firstName: string;
  preferredName: string | null;
}): string {
  return `${r.lastName}, ${r.firstName}${r.preferredName ? ` (${r.preferredName})` : ''}`;
}

/* ---------- patient-scoped routes ---------- */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PATIENT_PATH = /^\/patients\/([0-9a-f-]{36})(\/.*)?$/i;

export interface RouteTarget {
  to: string;
  params?: Record<string, string>;
  search: Record<string, unknown>;
}

/**
 * The same screen for another patient, or `null` when the screen is not
 * about one patient (`/patients/<id>/...`, `?patient=<id>`, `?account=<id>`).
 * Keeps the rest of the URL, so "stay" keeps the module and its view.
 */
export function samePlaceFor(
  pathname: string,
  search: Readonly<Record<string, unknown>>,
  patientId: string,
): RouteTarget | null {
  const m = PATIENT_PATH.exec(pathname);
  if (m && UUID.test(m[1] ?? '')) {
    return { to: `/patients/${patientId}${m[2] ?? ''}`, search: { ...search } };
  }
  for (const key of ['patient', 'account'] as const) {
    const v = search[key];
    if (typeof v === 'string' && UUID.test(v)) {
      return { to: pathname, search: { ...search, [key]: patientId } };
    }
  }
  return null;
}

/** `patient.open.<target>`: where each opens and who may open it. */
export const PATIENT_OPEN_TARGETS = {
  family: { label: 'Family File', permission: 'patient.view', path: '/patients/$patientId' },
  account: { label: 'Account', permission: 'patient.view', path: '/patients/$patientId' },
  chart: { label: 'Chart', permission: 'chart.view', path: '/patients/$patientId/chart' },
  perio: { label: 'Perio', permission: 'perio.view', path: '/patients/$patientId/perio' },
  txplan: {
    label: 'Treatment Plan',
    permission: 'txplan.view',
    path: '/patients/$patientId/treatment',
  },
  images: { label: 'Images', permission: 'imaging.view', path: '/imaging' },
  notes: { label: 'Clinical Notes', permission: 'notes.view', path: '/patients/$patientId/notes' },
} as const;
export type PatientOpenTarget = keyof typeof PATIENT_OPEN_TARGETS;
export const PATIENT_OPEN_TARGET_IDS = Object.keys(PATIENT_OPEN_TARGETS) as PatientOpenTarget[];

/** The route `patient.open.<target>` navigates to, keeping the office selector. */
export function openTargetRoute(
  target: PatientOpenTarget,
  patientId: string,
  office: unknown,
): RouteTarget {
  const keep = office === undefined ? {} : { office };
  switch (target) {
    case 'family':
      return { to: '/patients/$patientId', params: { patientId }, search: keep };
    case 'account':
      return {
        to: '/patients/$patientId',
        params: { patientId },
        search: { ...keep, tab: 'ledger' },
      };
    case 'images':
      return { to: '/imaging', search: { ...keep, patient: patientId } };
    default:
      return {
        to: PATIENT_OPEN_TARGETS[target].path,
        params: { patientId },
        search: keep,
      };
  }
}

/**
 * Where picking a patient opens by default (TC role test fix 19): a
 * treatment coordinator lands on the patient's treatment plans; everyone
 * else (or anyone who also holds a broader role) on the Family File.
 */
export function defaultOpenTarget(
  roleKeys: readonly string[],
  canTxPlan: boolean,
): PatientOpenTarget {
  const broader = ['owner', 'office_manager', 'front_desk', 'dentist', 'hygienist', 'billing'];
  return canTxPlan &&
    roleKeys.includes('treatment_coordinator') &&
    !roleKeys.some((k) => broader.includes(k))
    ? 'txplan'
    : 'family';
}

/** The patient before the current one in the recent list (Eaglesoft F5 "Last Person"). */
export function lastPatientId(
  recent: readonly { id: string }[],
  currentId: string | null,
): string | null {
  return recent.find((p) => p.id !== currentId)?.id ?? null;
}

/** A recent row into the model's hit, names masked by the caller's privacy function. */
export function rowToHit(r: SelectPatientRow, mask: (s: string) => string) {
  return {
    id: r.id,
    label: mask(selectLabel(r)),
    chartNumber: r.chartNumber,
    birthdate: r.birthdate,
    status: r.status,
    lastName: mask(r.lastName),
    firstName: mask(r.firstName),
    middleInitial: r.middleInitial,
    preferredName: r.preferredName === null ? null : mask(r.preferredName),
    age: r.age,
    headOfHouse: r.headOfHouse ? mask(r.headOfHouse.name) : null,
    isGuarantor: r.isGuarantor,
    homePhone: r.homePhone,
    workPhone: r.workPhone,
    cellPhone: r.cellPhone,
    email: r.email,
    addressLine1: r.addressLine1,
    city: r.city,
    state: r.state,
    postalCode: r.postalCode,
    pmsId: r.pmsId,
    lastVisitDate: r.lastVisitDate,
    ssnLast4: r.ssnLast4,
  };
}
